import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { ConnectedRouter } from "connected-react-router";
import "lib/polyfills.js";
import "./index.css";
import "views/style.css";
import "views/colors.css";
import "views/globalVars.css";
import registerServiceWorker from "./registerServiceWorker";
import history from "./state/history";
import store from "./state/store";
import Routes from "./routes";
import { default as UpgradeModal } from "views/containers/UpgradeModalContainer";
import { default as ConfirmationModal } from "views/containers/ConfirmationModalContainer";
import { default as CollectionPreviewModal } from "views/containers/CollectionPreviewModalContainer";
import { default as RequestApprovalModal } from "views/containers/RequestApprovalModalContainer";
import { default as ApprovalModal } from "views/components/ApprovalModal/ApprovalModalContainer";
import { default as PurchaseCollectionModal } from "views/components/PurchaseCollection/PurchaseCollectionModalContainerGlobal.js";
import {
  sessionRecorder,
  toaster,
  appsignal,
  initializeGlobalErrorHandlers,
  createTestErrorHelper
} from "lib";
import { ErrorBoundary } from "@appsignal/react";
import Copymaker from "lib/Copymaker";
import InputMode from "lib/InputMode";
import Render from "lib/Render";
import SSO from "lib/SSO";
import ApplicationUpdateNotifier from "lib/ApplicationUpdateNotifier/ApplicationUpdateNotifier";

import { default as ConfirmationModalLib } from "lib/ConfirmationModal/ConfirmationModal";

console.log("[SW Test] Vite migration build");

if (!Render.isRenderMode()) {
  SSO.init();

  ConfirmationModalLib.config({ store });

  // SessionRewind only runs in production (handled internally by sessionRecorder.init)
  sessionRecorder.init();
  toaster.initialize();

  // Initialize global error handlers for AppSignal
  // Redux context is captured by the middleware and accessed by error handlers
  initializeGlobalErrorHandlers(appsignal);

  // Create test error helper for browser console testing
  createTestErrorHelper(appsignal);

  Copymaker.startModeIfNeeded();
  InputMode.startModeIfNeeded();
}

const Modals = () => {
  return (
    <React.Fragment>
      <UpgradeModal />
      <ConfirmationModal />
      <CollectionPreviewModal />
      <RequestApprovalModal />
      <ApprovalModal />
      <PurchaseCollectionModal />
    </React.Fragment>
  );
};

const Root = () => {
  return (
    <span className="notranslate">
      <Provider store={store}>
        <ConnectedRouter history={history}>
          <React.Fragment>
            <Routes />
            <Modals />
          </React.Fragment>
        </ConnectedRouter>
      </Provider>
    </span>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <ErrorBoundary instance={appsignal} action="Easil Web UI">
    <Root />
  </ErrorBoundary>
);

registerServiceWorker();

// Add event listeners on location change. This ensures that SPA navigation triggers a service
// worker update check but like a browser navigation event would.
history.listen((location, action) => {
  if (navigator.serviceWorker) {
    navigator.serviceWorker.ready.then(registration => {
      console.info("Checking server for updated service worker");
      registration.update().catch(error => {
        console.info(
          "Service worker update check failed (will retry on next navigation):",
          error.message
        );
      });
    });

    // Deferred SW update + reload policy
    // ------------------------------------------------------------
    // We DO NOT reload immediately when a new service worker is
    // detected. Instead, we reload on the NEXT route change. This
    // keeps the current user flow uninterrupted.
    //
    // Detection: `registerServiceWorker()` sets
    // `state.ui.updateNotification.visible = true` when a new SW is
    // installed and waiting. We look for that flag here.
    //
    // Behavior on next route change (first one only):
    //  - If there is a waiting worker, attach a one-off
    //    `controllerchange` handler to reload when the new worker
    //    takes control, then trigger SKIP_WAITING via
    //    `ApplicationUpdateNotifier.dispatchNotification()`.
    //  - If there is no waiting worker (e.g., it already activated
    //    via another tab), reload immediately.
    const state = store.getState();
    const shouldApplyUpdate =
      state &&
      state.ui &&
      state.ui.updateNotification &&
      state.ui.updateNotification.visible;
    if (shouldApplyUpdate) {
      // Standard path: we detected an update earlier in this tab.
      if (window.__swReloading__) {
        return;
      }
      navigator.serviceWorker
        .getRegistration()
        .then(reg => {
          if (reg && reg.waiting) {
            const onControllerChange = () => {
              if (window.__swReloading__) return;
              window.__swReloading__ = true;
              console.info(
                "[SW Debug] Controller changed! Reloading application NOW"
              );
              window.location.reload();
            };
            navigator.serviceWorker.addEventListener(
              "controllerchange",
              onControllerChange,
              { once: true }
            );
            // Trigger SKIP_WAITING (and clear the Redux flag).
            ApplicationUpdateNotifier.dispatchNotification();
          } else {
            // No waiting worker: likely already active from elsewhere.
            // Reload now as part of this navigation cycle.
            window.__swReloading__ = true;
            window.location.reload();
          }
        })
        .catch(err => {
          // If we can't inspect the registration, fail-safe reload.
          window.__swReloading__ = true;
          window.location.reload();
        });
    } else {
      // Independent detection per tab:
      // Even if this tab never saw the "waiting" state (e.g., another tab
      // already activated the new SW), detect it by comparing the active SW
      // for the registration against our current controller. If they differ,
      // queue a reload on this navigation.
      navigator.serviceWorker
        .getRegistration()
        .then(reg => {
          if (!reg) return;
          const ctrl = navigator.serviceWorker.controller;
          const active = reg.active;
          if (ctrl && active) {
            const ctrlUrl = ctrl.scriptURL || "";
            const activeUrl = active.scriptURL || "";
            const differs = activeUrl && ctrlUrl && activeUrl !== ctrlUrl;
            const objectsDiffer = active !== ctrl; // extra safety
            if ((differs || objectsDiffer) && !window.__swReloading__) {
              window.__swReloading__ = true;
              console.info(
                "New active service worker detected on navigation. Reloading application"
              );
              window.location.reload();
            }
          }
        })
        .catch(() => {
          // No action; best-effort detection only.
        });
    }
  }

  // update inline manual when the page is changed
  if (window.inline_manual_player) {
    window.inline_manual_player.update();
  }
});
